import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getApplicationRouteTitle,
  getPublicRouteMetadata,
  isNoindexApplicationPath,
  SITE_METADATA,
} from './publicRouteMetadata';

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function upsertCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = href;
}

function removeCanonical() {
  document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
}

export function PublicRouteHead() {
  const location = useLocation();

  useEffect(() => {
    const route = getPublicRouteMetadata(location.pathname);
    if (!route) {
      upsertMeta('name', 'robots', 'noindex,nofollow');
      if (isNoindexApplicationPath(location.pathname)) {
        document.title = getApplicationRouteTitle(location.pathname);
        removeCanonical();
      } else {
        document.title = 'Page Not Found | BeatBandit';
        upsertCanonical(`${SITE_METADATA.origin}${location.pathname}`);
      }
      return;
    }

    const canonical = `${SITE_METADATA.origin}${route.canonicalPath === '/' ? '/' : route.canonicalPath}`;
    document.title = route.title;
    upsertMeta('name', 'description', route.description);
    upsertMeta('name', 'robots', route.robots);
    upsertMeta('property', 'og:title', route.title);
    upsertMeta('property', 'og:description', route.description);
    upsertMeta('property', 'og:type', route.type);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', route.image);
    upsertMeta('name', 'twitter:title', route.title);
    upsertMeta('name', 'twitter:description', route.description);
    upsertMeta('name', 'twitter:image', route.image);
    upsertCanonical(canonical);

    let structuredData = document.getElementById('route-structured-data') as HTMLScriptElement | null;
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'route-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify(route.structuredData);
  }, [location.pathname]);

  return null;
}
